package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initABrokerThatFitsInOneHeadPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ABrokerThatFitsInOneHead.md", FrontMatterElement.Builder { addScalar("title", "A broker that fits in one head"); addScalar("searchTitle", "Building a message broker in Kotlin on an append-only log"); addScalar("date", "2026-06-21"); addScalar("description", "booblik reproduces what makes an append-only log fast and leaves out everything that makes Kafka a cluster — including three features that are incompatible with zero-copy by construction."); addScalar("library", "booblik"); }))
}

@Page("/blog/a-broker-that-fits-in-one-head")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ABrokerThatFitsInOneHeadPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/booblik") {
            org.jetbrains.compose.web.dom.Text("booblik")
        }
        org.jetbrains.compose.web.dom.Text(" is a message broker on the JVM built around an append-only log: a topic splits into partitions, a partition into segments, and a consumer reads by a numeric offset it keeps itself.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The niche is not \"a Kafka replacement\". It is a broker that fits in one head and one process — no ZooKeeper, no KRaft quorum, no group coordinator. Everything that makes Kafka ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("operable as a cluster")
        }
        org.jetbrains.compose.web.dom.Text(" is deliberately absent; everything that makes a log ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("fast")
        }
        org.jetbrains.compose.web.dom.Text(" is reproduced and measured.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-writer-is-an-actor-and-it-has-no-timer") }) {
        org.jetbrains.compose.web.dom.Text("The writer is an actor, and it has no timer")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Batching writes is the obvious optimisation and the usual implementation is a timer: hold writes for 5 ms, then flush. Which means you have configured a latency floor, and you now own the job of retuning it whenever load changes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("booblik has one writer actor per partition, and it groups ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("whatever is already in its mailbox")
        }
        org.jetbrains.compose.web.dom.Text(" into a single barrier. No timer, no window.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Under light load the mailbox holds one message and it goes out immediately. Under heavy load it holds hundreds and they go out together. The group size self-adjusts because it ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" the load, measured at the only moment that matters — the instant before the write.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("zero-copy-and-what-it-costs") }) {
        org.jetbrains.compose.web.dom.Text("Zero-copy, and what it costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("FETCH is served straight from the page cache to the socket with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("transferTo")
        }
        org.jetbrains.compose.web.dom.Text(". The bytes never enter the JVM heap. Below that sit segments with a sparse offset index, two write paths (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FileChannel")
        }
        org.jetbrains.compose.web.dom.Text(" and an FFM memory mapping), rolling, retention by size and by age, and recovery from a torn write.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A caught-up consumer does not poll. It waits on the broker, and a write during the wait wakes it immediately.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every record carries a CRC32C, verified on every read that touches the bytes — which, by construction, the zero-copy path does not. That is not an oversight, it is the trade: you cannot check bytes you never look at. Saying so out loud is more useful than a footnote claiming end-to-end verification.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two more things are absent for the same reason. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("TLS")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("compression")
        }
        org.jetbrains.compose.web.dom.Text(" both require transforming the bytes, and transforming them means copying them. They are not on a roadmap because they are incompatible with the design, not because nobody got to them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Topic creation at runtime is missing on purpose too: the set of partitions is fixed at startup, which is what removes the coordinator.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("measured-not-asserted") }) {
        org.jetbrains.compose.web.dom.Text("Measured, not asserted")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The performance claims come from a rented Linux machine with ext4 on a local disk and nothing else running — and, for the end-to-end figures, from two such machines with a 7.85 Gbit/s link between them, the broker in its own process and the load generator on the other host.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That last detail is the one that makes the numbers mean anything. A generator sharing cores with the service measures the generator; I have made that mistake elsewhere and written it up. Methodology and full tables are in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/booblik") {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("docs/benchmarking.md")
            }
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-ships") }) {
        org.jetbrains.compose.web.dom.Text("What ships")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A client library with a pipelined connection matched by correlation id, an accumulating producer, a topic handle that routes by key, and subscriptions as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Flow<RecordBatch>")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A runnable broker with configuration validated at startup, a metrics line, retention, a distribution and a container image with its own health check — because \"it runs on my laptop\" and \"it starts in a container with the wrong config and tells you why\" are different levels of finished.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/booblik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/booblik")
        }
    }
}
